import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useFormState, useWatch } from 'react-hook-form';
import {
  AutocompleteSingleSelect,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  Input,
  Label,
  toast,
} from '@shigo-ui/ui';

import { useBusyDialogGuard } from '../../../../hooks/useBusyDialogGuard';
import { dashboardT } from '../../../../lib/messages';
import {
  useDebugDemandPartnerOptions,
  useDebugPlacementOptions,
  useDebugPublisherOptions,
  useDebugRuleMutations,
} from '../../hooks';
import {
  DebugEventType,
  DebugSourceType,
  debugEventTypeRequestMap,
  debugLookupLabel,
  debugRuleEventTypes,
  isDebugRuleEventType,
  type DebugLookupOption,
  type DebugRule,
  type DebugRuleRequest,
} from '../../model';
import { debugRuleDialogSchema, type DebugRuleDialogFormValue } from './DebugRuleDialog.schema';

interface DebugRuleDialogProps {
  rule: DebugRule | null;
  onClose: () => void;
}

const eventTypeOptions = debugRuleEventTypes;

export function DebugRuleDialog({ rule, onClose }: DebugRuleDialogProps) {
  'use no memo';

  const publisherSource = sourceFor(rule, DebugSourceType.Publisher);
  const placementSource = sourceFor(rule, DebugSourceType.Placement);
  const demandPartnerSource = sourceFor(rule, DebugSourceType.DemandPartner);
  const selectedEventTypes = rule?.eventTypes.filter(isDebugRuleEventType) ?? [];
  const hasUnknownEventTypes = rule?.eventTypes.includes(DebugEventType.Unknown) ?? false;
  const hasUnknownSourceTypes =
    rule?.sources.some((source) => source.sourceType === DebugSourceType.Unknown) ?? false;
  const hasUnsupportedPersistedValues = hasUnknownEventTypes || hasUnknownSourceTypes;
  const form = useForm<DebugRuleDialogFormValue>({
    resolver: zodResolver(debugRuleDialogSchema),
    defaultValues: {
      publisherId: publisherSource?.id ?? '',
      publisherSecondaryId: publisherSource?.secondaryId ?? '',
      placementId: placementSource?.id ?? '',
      placementSecondaryId: placementSource?.secondaryId ?? '',
      demandPartnerId: demandPartnerSource?.id ?? '',
      sampleRate: rule?.sampleRate ?? 1,
      durationSeconds: durationSecondsFor(rule),
      eventTypes: rule ? selectedEventTypes : [...eventTypeOptions],
      enabled: rule?.enabled ?? true,
    },
  });
  const { errors } = useFormState({ control: form.control });
  const publisherId = useWatch({ control: form.control, name: 'publisherId' });
  const placementId = useWatch({ control: form.control, name: 'placementId' });
  const demandPartnerId = useWatch({ control: form.control, name: 'demandPartnerId' });
  const publisherSecondaryId = useWatch({
    control: form.control,
    name: 'publisherSecondaryId',
  });
  const placementSecondaryId = useWatch({
    control: form.control,
    name: 'placementSecondaryId',
  });
  const [publisherSearch, setPublisherSearch] = React.useState('');
  const [placementSearch, setPlacementSearch] = React.useState('');
  const [demandPartnerSearch, setDemandPartnerSearch] = React.useState('');
  const [selectedPublisher, setSelectedPublisher] = React.useState<DebugLookupOption | undefined>(
    () => lookupOptionForSource(publisherSource),
  );
  const [selectedPlacement, setSelectedPlacement] = React.useState<DebugLookupOption | undefined>(
    () => lookupOptionForSource(placementSource),
  );
  const [selectedDemandPartner, setSelectedDemandPartner] = React.useState<
    DebugLookupOption | undefined
  >(() => lookupOptionForSource(demandPartnerSource));
  const publishers = useDebugPublisherOptions(publisherSearch, publisherId);
  const placements = useDebugPlacementOptions(placementSearch, publisherId, placementId);
  const demandPartners = useDebugDemandPartnerOptions(demandPartnerSearch, demandPartnerId);
  const mutations = useDebugRuleMutations();
  const pending = mutations.create.isPending || mutations.update.isPending;
  const controlsDisabled = pending || hasUnsupportedPersistedValues;
  const guard = useBusyDialogGuard(pending, onClose);
  const demandPartnerOnly =
    Boolean(demandPartnerId) && !publisherSecondaryId && !placementSecondaryId;

  React.useEffect(() => {
    if (!demandPartnerOnly || hasUnsupportedPersistedValues) return;
    form.setValue('eventTypes', [DebugEventType.DpRequest], { shouldDirty: true });
    form.clearErrors('eventTypes');
  }, [demandPartnerOnly, form, hasUnsupportedPersistedValues]);

  React.useEffect(() => {
    if (publisherSecondaryId || !publisherId) return;
    const hydrated = publishers.data?.content.find((option) => option.id === publisherId);
    if (!hydrated?.secondaryId) return;
    form.setValue('publisherSecondaryId', hydrated.secondaryId);
  }, [form, publisherId, publisherSecondaryId, publishers.data?.content]);

  React.useEffect(() => {
    if (placementSecondaryId || !placementId) return;
    const hydrated = placements.data?.content.find((option) => option.id === placementId);
    if (!hydrated?.secondaryId) return;
    form.setValue('placementSecondaryId', hydrated.secondaryId);
  }, [form, placementId, placementSecondaryId, placements.data?.content]);

  async function submit(values: DebugRuleDialogFormValue) {
    if (hasUnsupportedPersistedValues) return;

    const payload: DebugRuleRequest = {
      ...(trimmed(values.publisherSecondaryId)
        ? { publisherSecondaryId: trimmed(values.publisherSecondaryId) }
        : {}),
      ...(trimmed(values.placementSecondaryId)
        ? { placementSecondaryId: trimmed(values.placementSecondaryId) }
        : {}),
      ...(trimmed(values.demandPartnerId)
        ? { demandPartnerId: trimmed(values.demandPartnerId) }
        : {}),
      ...(values.sampleRate === undefined ? {} : { sampleRate: values.sampleRate }),
      ...(values.durationSeconds === undefined ? {} : { durationSeconds: values.durationSeconds }),
      eventTypes: values.eventTypes.map((eventType) => debugEventTypeRequestMap[eventType]),
      enabled: values.enabled,
    };

    try {
      if (rule) await mutations.update.mutateAsync({ jobId: rule.jobId, payload });
      else await mutations.create.mutateAsync(payload);
      toast.success(dashboardT(rule ? 'debug.updated' : 'debug.created'));
      onClose();
    } catch {
      form.setError('root.server', { message: dashboardT('debug.saveError') });
    }
  }

  const publisherOptions = mergeOptions(selectedPublisher, publishers.data?.content);
  const placementOptions = mergeOptions(selectedPlacement, placements.data?.content);
  const demandPartnerOptions = mergeOptions(selectedDemandPartner, demandPartners.data?.content);

  return (
    <Dialog open onOpenChange={guard.onOpenChange}>
      <DialogContent
        className="max-w-[820px] gap-0 overflow-hidden p-0"
        onEscapeKeyDown={guard.onDismissInteraction}
        onPointerDownOutside={guard.onDismissInteraction}
      >
        <DialogHeader className="border-border border-b px-5 py-3.5">
          <DialogTitle className="text-[15px] font-semibold">
            {dashboardT(rule ? 'debug.form.editTitle' : 'debug.form.createTitle')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {dashboardT('debug.form.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={(event) => void form.handleSubmit(submit)(event)} noValidate>
            <div className="max-h-[72vh] space-y-6 overflow-y-auto px-5 py-4">
              <section className="space-y-3">
                <h2 className="text-sm font-semibold">{dashboardT('debug.form.sources')}</h2>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-[13px] font-semibold">
                      {dashboardT('debug.form.publisher')}
                    </Label>
                    <Controller
                      control={form.control}
                      name="publisherId"
                      render={({ field }) => (
                        <AutocompleteSingleSelect
                          label={dashboardT('debug.form.publisher')}
                          value={field.value}
                          options={publisherOptions.map(toAutocompleteOption)}
                          onSearchTermChange={setPublisherSearch}
                          searchDebounceMs={650}
                          minSearchLength={1}
                          loading={publishers.isFetching}
                          placeholder={dashboardT('debug.form.searchPublisher')}
                          searchPlaceholder={dashboardT('debug.form.searchPublisher')}
                          emptyMessage={dashboardT('debug.form.noPublishers')}
                          disabled={controlsDisabled}
                          onValueChange={(nextId) => {
                            const option = publisherOptions.find(({ id }) => id === nextId);
                            field.onChange(nextId);
                            form.setValue('publisherSecondaryId', option?.secondaryId ?? '', {
                              shouldDirty: true,
                            });
                            setSelectedPublisher(option);
                            form.setValue('placementId', '', { shouldDirty: true });
                            form.setValue('placementSecondaryId', '', { shouldDirty: true });
                            setSelectedPlacement(undefined);
                          }}
                        />
                      )}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[13px] font-semibold">
                      {dashboardT('debug.form.placement')}
                    </Label>
                    <Controller
                      control={form.control}
                      name="placementId"
                      render={({ field }) => (
                        <AutocompleteSingleSelect
                          label={dashboardT('debug.form.placement')}
                          value={field.value}
                          options={placementOptions.map(toAutocompleteOption)}
                          onSearchTermChange={setPlacementSearch}
                          searchDebounceMs={650}
                          minSearchLength={1}
                          loading={placements.isFetching}
                          placeholder={dashboardT('debug.form.searchPlacement')}
                          searchPlaceholder={dashboardT('debug.form.searchPlacement')}
                          emptyMessage={dashboardT('debug.form.noPlacements')}
                          disabled={controlsDisabled}
                          onValueChange={(nextId) => {
                            const option = placementOptions.find(({ id }) => id === nextId);
                            field.onChange(nextId);
                            form.setValue('placementSecondaryId', option?.secondaryId ?? '', {
                              shouldDirty: true,
                            });
                            setSelectedPlacement(option);
                          }}
                        />
                      )}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[13px] font-semibold">
                      {dashboardT('debug.form.demandPartner')}
                    </Label>
                    <Controller
                      control={form.control}
                      name="demandPartnerId"
                      render={({ field }) => (
                        <AutocompleteSingleSelect
                          label={dashboardT('debug.form.demandPartner')}
                          value={field.value}
                          options={demandPartnerOptions.map(toAutocompleteOption)}
                          onSearchTermChange={setDemandPartnerSearch}
                          searchDebounceMs={650}
                          minSearchLength={1}
                          loading={demandPartners.isFetching}
                          placeholder={dashboardT('debug.form.searchDemandPartner')}
                          searchPlaceholder={dashboardT('debug.form.searchDemandPartner')}
                          emptyMessage={dashboardT('debug.form.noDemandPartners')}
                          disabled={controlsDisabled}
                          onValueChange={(nextId) => {
                            const option = demandPartnerOptions.find(({ id }) => id === nextId);
                            field.onChange(nextId);
                            setSelectedDemandPartner(option);
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
                {errors.publisherId?.message ? (
                  <p className="text-destructive text-xs" role="alert">
                    {errors.publisherId.message}
                  </p>
                ) : null}
                {hasUnknownSourceTypes ? (
                  <p className="text-muted-foreground text-xs" role="status">
                    {dashboardT('debug.form.unsupportedSourceTypes')}
                  </p>
                ) : null}
              </section>

              <section className="space-y-3">
                <h2 className="text-sm font-semibold">{dashboardT('debug.form.settings')}</h2>
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-[13px] font-semibold" htmlFor="debug-sample-rate">
                      {dashboardT('debug.sampleRate')}
                    </Label>
                    <Input
                      id="debug-sample-rate"
                      type="number"
                      min={0}
                      max={1}
                      step={0.01}
                      disabled={controlsDisabled}
                      aria-invalid={Boolean(errors.sampleRate)}
                      aria-describedby={errors.sampleRate ? 'debug-sample-rate-error' : undefined}
                      {...form.register('sampleRate', { setValueAs: optionalNumberInput })}
                    />
                    {errors.sampleRate?.message ? (
                      <p
                        id="debug-sample-rate-error"
                        className="text-destructive text-xs"
                        role="alert"
                      >
                        {errors.sampleRate.message}
                      </p>
                    ) : null}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[13px] font-semibold" htmlFor="debug-duration">
                      {dashboardT('debug.form.duration')}
                    </Label>
                    <Input
                      id="debug-duration"
                      type="number"
                      min={1}
                      step={1}
                      disabled={controlsDisabled}
                      aria-invalid={Boolean(errors.durationSeconds)}
                      aria-describedby={errors.durationSeconds ? 'debug-duration-error' : undefined}
                      {...form.register('durationSeconds', {
                        setValueAs: optionalNumberInput,
                      })}
                    />
                    {errors.durationSeconds?.message ? (
                      <p
                        id="debug-duration-error"
                        className="text-destructive text-xs"
                        role="alert"
                      >
                        {errors.durationSeconds.message}
                      </p>
                    ) : null}
                  </div>
                </div>
                <Controller
                  control={form.control}
                  name="enabled"
                  render={({ field }) => (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="debug-enabled"
                        checked={field.value}
                        disabled={controlsDisabled}
                        onCheckedChange={(checked) => {
                          field.onChange(checked === true);
                        }}
                      />
                      <Label htmlFor="debug-enabled">{dashboardT('debug.enabled')}</Label>
                    </div>
                  )}
                />
              </section>

              <section className="space-y-3">
                <h2 className="text-sm font-semibold">{dashboardT('debug.eventTypes')}</h2>
                <Controller
                  control={form.control}
                  name="eventTypes"
                  render={({ field }) => (
                    <div className="grid gap-3 sm:grid-cols-2">
                      {eventTypeOptions.map((eventType) => {
                        const disabled =
                          controlsDisabled ||
                          (demandPartnerOnly && eventType !== DebugEventType.DpRequest);
                        return (
                          <div key={eventType} className="flex items-center gap-2">
                            <Checkbox
                              id={`debug-event-${eventType}`}
                              checked={field.value.includes(eventType)}
                              disabled={disabled}
                              onCheckedChange={(checked) => {
                                field.onChange(
                                  checked === true
                                    ? [...new Set([...field.value, eventType])]
                                    : field.value.filter((value) => value !== eventType),
                                );
                              }}
                            />
                            <Label htmlFor={`debug-event-${eventType}`}>{eventType}</Label>
                          </div>
                        );
                      })}
                    </div>
                  )}
                />
                {errors.eventTypes?.message ? (
                  <p className="text-destructive text-xs" role="alert">
                    {errors.eventTypes.message}
                  </p>
                ) : null}
                {hasUnknownEventTypes ? (
                  <p className="text-muted-foreground text-xs" role="status">
                    {dashboardT('debug.form.unsupportedEventTypes')}
                  </p>
                ) : null}
              </section>

              {errors.root?.server?.message ? (
                <p className="text-destructive text-sm" role="alert">
                  {errors.root.server.message}
                </p>
              ) : null}
            </div>
            <DialogFooter className="border-border border-t px-5 py-3">
              <Button type="button" variant="outline" disabled={pending} onClick={onClose}>
                {dashboardT('debug.cancel')}
              </Button>
              <Button type="submit" disabled={controlsDisabled}>
                {pending
                  ? dashboardT('debug.saving')
                  : dashboardT(rule ? 'debug.update' : 'debug.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function sourceFor(rule: DebugRule | null, sourceType: DebugSourceType) {
  return rule?.sources.find((source) => source.sourceType === sourceType);
}

function lookupOptionForSource(source: ReturnType<typeof sourceFor>) {
  if (!source?.id) return undefined;
  return {
    id: source.id,
    name: source.name,
    secondaryId: source.secondaryId,
  };
}

function durationSecondsFor(rule: DebugRule | null) {
  if (!rule?.enabledUntil) return 300;
  const enabledUntil = new Date(rule.enabledUntil).getTime();
  return Number.isNaN(enabledUntil)
    ? 300
    : Math.max(1, Math.ceil((enabledUntil - Date.now()) / 1000));
}

function trimmed(value: string | undefined) {
  return value?.trim() ?? '';
}

function optionalNumberInput(value: string) {
  return value === '' ? undefined : Number(value);
}

function mergeOptions(
  selected: DebugLookupOption | undefined,
  options: readonly DebugLookupOption[] | undefined,
) {
  const merged = selected ? [...(options ?? []), selected] : [...(options ?? [])];
  return merged.filter((option, index) => merged.findIndex(({ id }) => id === option.id) === index);
}

function toAutocompleteOption(option: DebugLookupOption) {
  return { id: option.id, label: debugLookupLabel(option) };
}
